// Log in (FR-013 to FR-017, FR-022, FR-047 to FR-049; R-1): email + password, Forgot password? and
// Sign up. Every outcome message comes from messages.ts. A successful sign-in hands the result to
// the session controller; the signed-out layout then redirects, so this screen never navigates.
import { Link, useLocalSearchParams } from "expo-router";
import { useState } from "react";
import { Platform, StyleSheet, View } from "react-native";

import { sanitizeReturnTo } from "@/session/access";
import { useCompleteSignIn, useSession } from "@/session/hooks";
import { makeStyles } from "@/theme/makeStyles";
import { useTheme } from "@/theme/useTheme";
import { useCooldown } from "@/lib/useCooldown";
import { useSubmitAction } from "@/lib/useSubmitAction";
import { Button } from "@/ui/Button";
import { Form } from "@/ui/Form";
import { LockIcon } from "@/ui/icons/LockIcon";
import { MailIcon } from "@/ui/icons/MailIcon";
import { InlineMessage } from "@/ui/InlineMessage";
import { LinkButton } from "@/ui/LinkButton";
import { PasswordField } from "@/ui/PasswordField";
import { TextField } from "@/ui/TextField";

import { useAuthApi } from "../AuthApiContext";
import { AUTH_COPY } from "../copy";
import { type AuthErrorView, fieldMessage, messageFor } from "../messages";
import { useAuthBack } from "@/shell/authFrame/AuthFrame";

import { AuthCard } from "../ui/AuthCard";
import { checkEmail, checkLoginPassword } from "../validation";

const RESEND_COOLDOWN_S = 60;

/** Phones name the app above the card and give the card's title no line under it. */
const PHONE = Platform.OS !== "web";

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    form: { gap: t.space.lg },
    links: {
      flexDirection: "row",
      flexWrap: "wrap",
      justifyContent: "space-between",
    },
  }),
);

type Problem =
  | { kind: "none" }
  | { kind: "refused"; text: string }
  | { kind: "view"; view: AuthErrorView };

export function LoginScreen() {
  const styles = useStyles();
  const onBack = useAuthBack();
  const theme = useTheme();
  const authApi = useAuthApi();
  const completeSignIn = useCompleteSignIn();
  const session = useSession();
  const { returnTo } = useLocalSearchParams<{ returnTo?: string }>();
  const safeReturnTo = sanitizeReturnTo(
    typeof returnTo === "string" ? returnTo : null,
  );

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fieldErrors, setFieldErrors] = useState<{
    email?: string;
    password?: string;
  }>({});
  const [problem, setProblem] = useState<Problem>({ kind: "none" });
  const [resent, setResent] = useState(false);
  const cooldown = useCooldown(RESEND_COOLDOWN_S);

  const signIn = useSubmitAction(async (address: string, pw: string) => {
    const auth = await authApi.signInWithEmail(address, pw);
    return completeSignIn(auth, { returnTo: safeReturnTo });
  });
  const resend = useSubmitAction((address: string) =>
    authApi.resendVerificationEmail(address),
  );

  const sessionEnded =
    session.status === "signedOut" &&
    session.reason === "SESSION_ENDED" &&
    problem.kind === "none";
  // FR-026: a sign-in or renewal that found no role this app serves.
  const noAccess =
    session.status === "signedOut" &&
    session.reason === "NO_ACCESS" &&
    problem.kind === "none";

  async function submit() {
    const e = checkEmail(email);
    const p = checkLoginPassword(password);
    const errors = {
      ...(e.ok ? {} : { email: fieldMessage("email", e.code) }),
      ...(p.ok ? {} : { password: fieldMessage("password", p.code) }),
    };
    setFieldErrors(errors);
    if (!e.ok || !p.ok) return;

    setResent(false);
    const result = await signIn.submit(e.value, p.value);
    if (result.status === "skipped") return;
    if (result.status === "done") {
      if (result.value.kind === "refused") {
        setPassword("");
        setProblem({ kind: "refused", text: result.value.message });
      } else {
        setProblem({ kind: "none" });
      }
      return;
    }
    const view = messageFor(result.error, "signInWithEmail");
    if (view.kind === "fields") {
      setFieldErrors({
        email: view.fields.email,
        password: view.fields.password,
      });
      setProblem({ kind: "none" });
      return;
    }
    // FR-013: the email stays, the password is typed again (except after a connection failure).
    if (view.kind !== "connection") setPassword("");
    setProblem({ kind: "view", view });
  }

  async function resendEmail() {
    const e = checkEmail(email);
    if (!e.ok || cooldown.active) return;
    const result = await resend.submit(e.value);
    if (result.status === "done") {
      setResent(true);
      cooldown.start();
    } else if (result.status === "failed") {
      setProblem({
        kind: "view",
        view: messageFor(result.error, "resendVerificationEmail"),
      });
    }
  }

  const returnParams = safeReturnTo ? { returnTo: safeReturnTo } : {};

  return (
    <AuthCard
      onBack={onBack}
      title={AUTH_COPY.logIn.title}
      subtitle={PHONE ? undefined : AUTH_COPY.logIn.subtitle}
      intro={PHONE ? AUTH_COPY.logIn.phoneIntro : AUTH_COPY.logIn.intro}
      testID="login-screen"
    >
      {sessionEnded ? (
        <InlineMessage tone="info">
          {AUTH_COPY.logIn.sessionEnded}
        </InlineMessage>
      ) : null}
      {noAccess ? (
        <InlineMessage tone="error">
          {AUTH_COPY.refusals.noAccess}
        </InlineMessage>
      ) : null}
      {problem.kind === "refused" ? (
        <InlineMessage tone="error">{problem.text}</InlineMessage>
      ) : null}
      {problem.kind === "view" && problem.view.kind === "banner" ? (
        <InlineMessage
          tone="error"
          detail={
            problem.view.reference
              ? AUTH_COPY.errors.reference(problem.view.reference)
              : null
          }
        >
          {problem.view.text}
        </InlineMessage>
      ) : null}
      {problem.kind === "view" && problem.view.kind === "connection" ? (
        <InlineMessage tone="warning">{problem.view.text}</InlineMessage>
      ) : null}
      {problem.kind === "view" && problem.view.kind === "notVerified" ? (
        <InlineMessage
          tone="warning"
          detail={resent ? AUTH_COPY.checkInbox.resent : null}
          action={
            <Button
              variant="outline"
              onPress={() => void resendEmail()}
              pending={resend.pending}
              disabled={cooldown.active}
            >
              {cooldown.active
                ? AUTH_COPY.actions.resendIn(cooldown.remaining)
                : AUTH_COPY.actions.resendEmail}
            </Button>
          }
        >
          {problem.view.text}
        </InlineMessage>
      ) : null}

      <Form
        onSubmit={() => void submit()}
        accessibilityLabel={AUTH_COPY.logIn.title}
      >
        <View style={styles.form}>
          <TextField
            label={AUTH_COPY.fields.email}
            kind="email"
            placeholder={AUTH_COPY.placeholders.email}
            icon={<MailIcon color={theme.color.textMuted} />}
            large
            value={email}
            onChangeText={setEmail}
            error={fieldErrors.email}
            testID="login-email"
          />
          <PasswordField
            label={AUTH_COPY.fields.password}
            placeholder={AUTH_COPY.placeholders.password}
            icon={<LockIcon color={theme.color.textMuted} />}
            large
            value={password}
            onChangeText={setPassword}
            error={fieldErrors.password}
            showLabel={AUTH_COPY.actions.showPassword}
            hideLabel={AUTH_COPY.actions.hidePassword}
            testID="login-password"
          />
          <Button
            onPress={() => void submit()}
            pending={signIn.pending}
            large
            testID="login-submit"
          >
            {AUTH_COPY.actions.logIn}
          </Button>
        </View>
      </Form>

      <View style={styles.links}>
        <Link href="/forgot-password" asChild>
          <LinkButton role="link">
            {AUTH_COPY.actions.forgotPassword}
          </LinkButton>
        </Link>
        <Link href={{ pathname: "/sign-up", params: returnParams }} asChild>
          <LinkButton role="link">{AUTH_COPY.actions.signUp}</LinkButton>
        </Link>
      </View>
    </AuthCard>
  );
}
